import { useNavigation } from '@react-navigation/native'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { CoachOnboardingPrompt } from 'pages/CoachOnboarding/subcomponents/CoachOnboardingPrompt'
import { ProgramQuestionRow } from 'pages/CoachOnboarding/subcomponents/ProgramQuestionRow'

import { BottomSheetNav } from 'widgets/BottomSheetNav'
import { Box } from 'widgets/Box'
import { FlatList } from 'widgets/FlatList'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'
import { useProgram } from 'hooks/useProgram'

import { links } from 'utils/constants'
import programQuestions from 'utils/programQuestions.json'

import { Program, ProgramTags, ProgramType } from 'types/program'

type ProgramQuestionsPageProps = {
  isInitialPage?: boolean
}

export const ProgramQuestionsPage = ({ isInitialPage = false }: ProgramQuestionsPageProps) => {
  const pageTitles = [
    {
      title: 'Who is this program for?',
      subTitle: 'Select all that apply.',
    },
    {
      title: 'What’s the goal of this program?',
      subTitle: 'Select up to three.',
    },
    {
      title: 'What are the specifics of this program?',
      subTitle: 'Select up to three.',
    },
    {
      title: 'What equipment level is required?',
      subTitle: 'Select one',
    },
    {
      title: 'How long is this program?',
      subTitle: 'Select one. The program will automatically repeat for customers when it is over.',
    },
    {
      title: 'What is the daily time commitment?',
      subTitle:
        'Select one. How much time can your customers expect to spend  on this program per day?',
    },
  ]

  const [programData, setProgramData] = useState<Program>({
    description: '',
    title: '',
    prices: [],
    type: ProgramType.PROGRAM,
    tags: {
      audience: [],
      goals: [],
      specifics: [],
      equipment: [],
      programDuration: [],
      sessionDuration: [],
    },
  })

  const [pageIndex, setPageIndex] = useState<number>(0)

  const { data: currentUser } = useCurrentUser()
  const { createProgram } = useProgram()

  const setupProgram = async () => {
    const response = await createProgram.mutateAsync(currentUser?.id)
    if (!response?.id) navigateBackOrHome()
    setProgramData({
      ...programData,
      id: response?.id ?? '',
    })
  }

  useEffect(() => {
    if (!programData?.id?.length) {
      setupProgram()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigation = useNavigation<any>()
  const navigateBackOrHome = useNavigateBackOrHome()
  const insets = useSafeAreaInsets()

  const pageData = useMemo(() => {
    return programQuestions.data[pageIndex].values as string[]
  }, [pageIndex])

  const maxSelection = useMemo(() => {
    return programQuestions.data[pageIndex].maxSelection as number
  }, [pageIndex])

  const handleOptionSelect = (option: string) => {
    const tagKeys = Object.keys(programData.tags!)
    const currentTagKey = tagKeys[pageIndex]

    const isSelected = isOptionSelected(option)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    let updatedTags = programData.tags[currentTagKey] as string[]

    if (!isSelected && maxSelection === 1) {
      updatedTags[0] = option
    } else if (!isSelected && (maxSelection === -1 || updatedTags.length < maxSelection)) {
      updatedTags.push(option)
    } else {
      updatedTags = updatedTags?.filter((item: string) => item !== option) ?? []
    }

    setProgramData({
      ...programData,
      tags: {
        ...programData.tags,
        [currentTagKey]: updatedTags,
      } as ProgramTags,
    })
  }

  const selectedTags = useMemo(() => {
    const tagKeys = Object.keys(programData.tags!)
    const currentTagKey = tagKeys[pageIndex]
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return programData.tags[currentTagKey]
  }, [pageIndex, programData.tags])

  const isOptionSelected = useCallback(
    (option: string) => {
      return selectedTags.includes(option)
    },
    [selectedTags],
  )

  const handleNext = () => {
    if (!selectedTags.length) return

    if (pageIndex < 5) {
      setPageIndex(pageIndex + 1)
      return
    }
    navigation.navigate(links.PROGRAM_DESCRIPTION_PAGE, {
      program: programData,
    })
  }

  const handleBack = () => {
    if (pageIndex > 0) {
      setPageIndex(pageIndex - 1)
      return
    }
    navigateBackOrHome()
  }

  return (
    <Box flex={1} backgroundColor='white' margin={{ bottom: insets.bottom }}>
      <BottomSheetNav
        withNext
        onBackPress={handleBack}
        onNextPress={handleNext}
        rightButtonTitle='Next'
        rightButtonColor={selectedTags.length && programData?.id ? 'v2primary' : 'grey'}
        withBack={!isInitialPage}
        backgroundColor='white'
      />
      <CoachOnboardingPrompt
        title={pageTitles[pageIndex].title}
        subtitle={pageTitles[pageIndex].subTitle}
        color='black'
      />
      <Box flex={1}>
        <FlatList<string>
          keyExtractor={(item) => item}
          data={pageData}
          renderItem={({ item }) => (
            <ProgramQuestionRow
              text={item}
              isSelected={isOptionSelected(item)}
              onPress={() => handleOptionSelect(item)}
            />
          )}
          style={{ paddingBottom: 24 }}
        />
      </Box>
    </Box>
  )
}
